/**
 * @file Lexique.tsx
 * @summary Page de l'application
 *
 * Route React Router déclarée dans `App.tsx`. Métadonnées SEO injectées via `<SEO/>` + prerender Puppeteer (cf. vite.config).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { useState, useMemo } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BookingCTA from "@/components/BookingCTA";
import SEO from "@/components/SEO";
import { buildSerpImageGraph, SERP_IMAGE_URLS } from "@/lib/serp-images";
import { Input } from "@/components/ui/input";
import { Search, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

interface Term { name: string; def: string; }
interface Category { title: string; terms: Term[]; }

const URL = "https://crossfitchistera.fr/lexique";

const lexiqueFaq = [
  { "@type": "Question", name: "Que signifie WOD ?", acceptedAnswer: { "@type": "Answer", text: "WOD signifie Workout Of the Day — l'entraînement du jour, programmé par les coachs." } },
  { "@type": "Question", name: "Quelle différence entre RX et Scaled ?", acceptedAnswer: { "@type": "Answer", text: "RX désigne le WOD réalisé exactement comme prescrit. Scaled est la version adaptée (charges, variantes) pour respecter l'intention du WOD selon le niveau." } },
  { "@type": "Question", name: "Que signifie 1RM ?", acceptedAnswer: { "@type": "Answer", text: "1RM signifie One Repetition Maximum : la charge maximale soulevable une seule fois avec une technique correcte." } },
  { "@type": "Question", name: "AMRAP, EMOM, For Time : c'est quoi ?", acceptedAnswer: { "@type": "Answer", text: "AMRAP : maximum de tours/répétitions dans un temps donné. EMOM : démarrer une tâche chaque minute. For Time : tout le WOD le plus vite possible." } },
];


const lexicon: Category[] = [
  {
    title: "La box et le cadre",
    terms: [
      { name: "Box", def: "Salle d'entraînement CrossFit. On dit « je vais à la box » plutôt que « à la salle »." },
      { name: "Drop-in", def: "Séance unique (sans abonnement dans une box où vous n'êtes pas abonné / inscrit), payable à l'unité, principalement réservée aux pratiquants de passage issus de box affiliées." },
      { name: "Séance d'essai", def: "Séance de découverte gratuite." },
      { name: "Resawod", def: "Outil de gestion dédié aux salles de sports (réservations, adhésions, paiements, reçus)." },
    ],
  },
  {
    title: "La séance et les WOD",
    terms: [
      { name: "WOD", def: "« Workout Of the Day » — l'entraînement du jour, programmation d'exercices prévue pour la séance." },
      { name: "Warm-up", def: "Échauffement. Phase indispensable pour préparer le corps et réduire le risque de blessure." },
      { name: "Skill", def: "Partie technique où l'on travaille un mouvement ou une partie « force » avec des charges précises." },
      { name: "Metcon", def: "« Metabolic Conditioning » — partie finale du WOD axée sur le conditionnement métabolique. Effort soutenu et chronométré qui développe l'endurance, la force et la résistance. Formats courants : AMRAP, EMOM, For Time, Tabata." },
      { name: "HIIT", def: "« High Intensity Interval Training » — entraînement fractionné à haute intensité." },
      { name: "Open CrossFit", def: "Compétition annuelle mondiale ouverte à tous les pratiquants, peu importe le niveau. 3 semaines, 3 WODs." },
    ],
  },
  {
    title: "Formats de WOD et scoring",
    terms: [
      { name: "EMOM", def: "« Every Minute On the Minute » — démarrer une tâche chaque minute, le temps restant sert de repos." },
      { name: "AMRAP", def: "« As Many Reps/Rounds As Possible » — faire le maximum de répétitions ou tours dans un temps donné." },
      { name: "Tabata", def: "20 secondes d'effort / 10 secondes de repos, répété 8 fois (4 minutes)." },
      { name: "For Time", def: "Réaliser tout le WOD le plus vite possible. Le score est le temps final." },
      { name: "Benchmark", def: "WOD « test » servant de référence pour mesurer la progression." },
      { name: "Rep", def: "Répétition — le nombre de fois que l'on exécute un mouvement." },
      { name: "RM", def: "« Repetition Max » — charge maximale pour un nombre donné de répétitions (1RM, 3RM, 5RM)." },
      { name: "PR", def: "« Personal Record » — record personnel sur une charge ou un score." },
      { name: "RX", def: "« As prescribed » — réalisé exactement comme prescrit, sans adaptation." },
      { name: "Scaled", def: "Version adaptée du WOD (charges, variantes) pour rester dans l'intention de l'entraînement." },
      { name: "Inter", def: "Niveau intermédiaire entre Scaled et RX, progression vers les standards complets." },
      { name: "Foundations", def: "Version la plus accessible du WOD, mouvements simplifiés et charges légères, idéale pour débuter." },
      { name: "No Rep", def: "Répétition non validée car le standard n'est pas respecté." },
    ],
  },
  {
    title: "Mouvements Gym",
    terms: [
      { name: "Pull-up", def: "Traction à la barre — départ bras tendus, menton au-dessus de la barre." },
      { name: "CTB (Chest to Bar)", def: "Traction où la poitrine vient au contact de la barre." },
      { name: "T2B (Toes to Bar)", def: "Suspension : amener les pointes de pieds à toucher la barre." },
      { name: "MU (Muscle-up)", def: "Mouvement de transition passant de la suspension au-dessus de la barre/anneaux." },
      { name: "Dips", def: "Sur barres/anneaux : descendre en fléchissant les coudes puis remonter." },
      { name: "HSPU", def: "« Handstand Push-up » — pompe en appui renversé." },
      { name: "HS Walk", def: "Déplacement en marche sur les mains." },
      { name: "Kipping", def: "Technique utilisant le balancier pour générer de l'élan." },
      { name: "Rope Climb", def: "Grimper à la corde — exercice complet de tirage, gainage et coordination." },
    ],
  },
  {
    title: "Mouvements Haltéro / Force",
    terms: [
      { name: "Deadlift (DL)", def: "Soulevé de terre — lever une charge du sol jusqu'à la position debout." },
      { name: "Front Squat (FS)", def: "Squat avec charge en position front rack (clavicules/épaules)." },
      { name: "OHS (Overhead Squat)", def: "Squat en maintenant la charge au-dessus de la tête, bras tendus." },
      { name: "Shoulder Press", def: "Développé strict au-dessus de la tête, sans aide des jambes." },
      { name: "Power Clean (PC)", def: "Épaulé « puissance » — réception haute, sans squat complet." },
      { name: "Snatch", def: "Arraché — charge du sol au-dessus de la tête en une séquence." },
      { name: "Clean & Jerk (C&J)", def: "Épaulé-jeté — charge aux épaules (clean) puis au-dessus de la tête (jerk)." },
    ],
  },
  {
    title: "Poids du corps et conditioning",
    terms: [
      { name: "Box Jump", def: "Saut sur une caisse. Hauteur variable, descente step-down ou avec rebond." },
      { name: "Burpees", def: "Enchaînement au sol puis retour debout avec saut final. Le classique du cardio !" },
      { name: "Push-up (PU)", def: "Pompe — corps gainé, descente contrôlée, remontée bras tendus." },
      { name: "Sit-ups", def: "Relevés de buste, codifiés selon la séance." },
      { name: "Lunges", def: "Fentes avant ou arrière, avec ou sans charge." },
      { name: "Pistol Squat", def: "Squat sur une jambe — force, équilibre et mobilité." },
      { name: "Wall Ball", def: "Lancer d'un ballon lesté vers une cible au mur, avec squat à chaque rep." },
      { name: "Wall Climb", def: "Depuis une pompe, monter les pieds au mur jusqu'à une position renversée." },
      { name: "DU (Double Under)", def: "Corde à sauter : un saut = deux tours de corde." },
      { name: "SU (Simple Under)", def: "Corde à sauter : un saut = un tour de corde." },
    ],
  },
  {
    title: "Matériel",
    terms: [
      { name: "DB (Dumbbell)", def: "Haltère, utilisé pour snatch, clean, press…" },
      { name: "KB (Kettlebell)", def: "Poids à poignée pour swings, cleans, jerks, snatchs…" },
      { name: "Sled", def: "Traîneau/chariot à pousser ou tirer avec une charge." },
      { name: "Row", def: "Rameur — effort cardio prescrit en calories, distance ou temps." },
      { name: "Echo Bike", def: "Vélo d'assault à bras et jambes, très exigeant en cardio." },
      { name: "Ski Erg", def: "Machine simulant le ski de fond, travail du haut du corps et cardio." },
      { name: "WORM", def: "Sac lesté géant à soulever en équipe — exercice de coordination et de force collective." },
      { name: "D-Ball", def: "Ballon lourd sans poignée, pour des exercices de type « atlas stone »." },
      { name: "Wall Ball", def: "Ballon lesté (médecine ball) pour les lancers au mur." },
    ],
  },
];

export default function Lexique() {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search.trim()) return lexicon;
    const q = search.toLowerCase();
    return lexicon
      .map((cat) => ({
        ...cat,
        terms: cat.terms.filter(
          (t) => t.name.toLowerCase().includes(q) || t.def.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.terms.length > 0);
  }, [search]);

  const allTerms = lexicon.flatMap((c) => c.terms);
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "DefinedTermSet",
      name: "Lexique CrossFit",
      url: URL,
      hasDefinedTerm: allTerms.map((t) => ({
        "@type": "DefinedTerm",
        name: t.name,
        description: t.def,
        inDefinedTermSet: URL,
      })),
    },
    { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: lexiqueFaq },
    buildSerpImageGraph(),
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Lexique CrossFit : WOD, AMRAP, EMOM, RX, PR, 1RM | Chistera"
        description="Lexique CrossFit illustré : WOD, AMRAP, EMOM, RX, scaled, PR, 1RM, snatch, clean & jerk, kipping. Ressource gratuite par CrossFit Chistera (Lyon Est)."
        canonical={URL}
        ogImages={SERP_IMAGE_URLS}
        jsonLd={jsonLd}
      />
      <Header />
       <main className="pt-24 pb-20">
        <div className="container max-w-[1200px] mx-auto px-4 md:px-8">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gold hover:underline font-body normal-case mb-6">
            <ArrowLeft size={16} />
            Revenir à l'accueil
          </Link>
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl text-foreground">Lexique CrossFit : comprendre les mots utilisés en box</h1>
            <div className="text-muted-foreground mt-4 font-body normal-case max-w-2xl mx-auto space-y-3 text-left sm:text-center">
              <p>
                Le CrossFit a son propre vocabulaire — un mélange d'acronymes anglo-saxons (<strong className="text-foreground">WOD</strong>, <strong className="text-foreground">AMRAP</strong>, <strong className="text-foreground">EMOM</strong>, <strong className="text-foreground">RX</strong>, <strong className="text-foreground">PR</strong>, <strong className="text-foreground">1RM</strong>), de noms de mouvements d'haltérophilie (snatch, clean &amp; jerk, deadlift) et d'exercices gymniques (muscle-up, kipping, HSPU). Ce lexique rassemble plus de 60 termes définis simplement, pour comprendre une programmation, suivre un cours coaché ou lire un benchmark sans rester bloqué sur le jargon.
              </p>
              <p>
                Conçu et maintenu par les coachs de <strong className="text-foreground">CrossFit Chistera</strong> à Saint-Pierre-de-Chandieu (Est lyonnais), il s'adresse aussi bien aux débutants qui découvrent leur première box qu'aux pratiquants confirmés qui veulent vérifier une définition. Utilise la barre de recherche ci-dessous ou parcours les catégories.
              </p>
            </div>
          </div>


          {/* Search */}
          <div className="max-w-md mx-auto mb-12 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Rechercher un terme…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Categories */}
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground font-body normal-case">Aucun résultat pour « {search} »</p>
          ) : (
            <div className="space-y-12">
              {filtered.map((cat) => (
                <div key={cat.title}>
                  <h2 className="text-2xl md:text-3xl text-foreground mb-6 border-b-2 border-gold/30 pb-2">{cat.title}</h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cat.terms.map((t) => (
                      <div key={t.name} className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <h3 className="text-lg text-foreground leading-tight">{t.name}</h3>
                        <p className="text-sm text-muted-foreground mt-1.5 font-body normal-case leading-relaxed">{t.def}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="container mx-auto px-4 max-w-4xl">
          <BookingCTA title="Prêt(e) à passer de la théorie à la pratique ?" />
        </div>
      </main>
      <Footer />
    </div>
  );
}
